import { ShiftsColumns } from "./columns";
import { z } from "zod";
import { ShiftPositionType } from "./shift_positions";
import { NoteSchema, ClockInOptionsSchema } from "./projects";

export const MealOptionSchema = z.object({
  type: z.enum(["UNPAID", "PAID"]).default("UNPAID"),
  paid_by: z.enum(["CLIENT", "TENANT"]).optional(),
  duration: z.number().default(30), // duration in minutes, defaults to 30 minutes
  after: z.number().default(0), // hours after shift start when break begins (0 = start)
});

export type MealOptionType = z.infer<typeof MealOptionSchema>;

export const SHIFT_VENUE_TYPES = ["Indoor", "Outdoor", "Unspecified"] as const;
export type ShiftVenueType = (typeof SHIFT_VENUE_TYPES)[number];

export const ShiftMetaSchema = z.object({
  dress_code_id: z.string().uuid().optional().nullable(),
  meal_option: z.array(MealOptionSchema).optional(),
  onsite_contact: z.string().uuid().optional().nullable(),
  onsite_contact_name: z.string().optional().nullable(),
  positions: z.array(z.any()).optional(),
  description: z.string().optional(),
  venue_type: z.enum(SHIFT_VENUE_TYPES).optional(),
  notes: z.array(NoteSchema).optional(),
  markets: z.array(z.string().uuid()).optional(),
  clock_in_options: ClockInOptionsSchema.optional(),
});

export type ShiftMetaType = z.infer<typeof ShiftMetaSchema>;

export type ShiftLocationType = {
  id: string;
  name: string;
  address?: { street?: string; city?: string; state?: string; zip?: string };
  instructions?: string;
};

export type ShiftStatusType = "PENDING" | "APPROVED" | "ADVANCED" | "CANCELED" | "CANCELED - BILLABLE" | "COMPLETE";

export type ShiftType = Omit<ShiftsColumns, "meta_data"> & {
  meta_data?: ShiftMetaType;
  status?: ShiftStatusType;
  project?: {
    id: string;
    name: string;
    location_id?: string;
    location?: ShiftLocationType;
    client_id?: string;
    client?: {
      id: string;
      name: string;
      client_abilities?: {
        ability: {
          id: string;
          name: string;
          description?: string;
        };
      }[];
      client_restrictions?: {
        restriction: {
          id: string;
          name: string;
          description?: string;
        };
      }[];
    };
    dress_code?: {
      id: string;
      name: string;
      description?: string;
    };
    project_abilities?: {
      ability: {
        id: string;
        name: string;
        description?: string;
      };
    }[];
    project_restrictions?: {
      restriction: {
        id: string;
        name: string;
        description?: string;
      };
    }[];
    description?: string;
    meta_data?: { description?: string };
  };
  location?: ShiftLocationType;
  shift_positions?: ShiftPositionType[];
  // Enriched dress code from lists.dress_codes (stored as meta_data.dress_code_id)
  dress_code?: {
    id: string;
    name: string;
    description?: string;
  };
  // Link table relationships
  shift_abilities?: {
    ability: {
      id: string;
      name: string;
      description?: string;
    };
  }[];
  shift_restrictions?: {
    restriction: {
      id: string;
      name: string;
      description?: string;
    };
  }[];
  shift_supervisors?: {
    id: string;
    user_id: string;
    user?: {
      id: string;
      first_name: string;
      last_name: string;
      email_address?: string;
      preferred_name?: string;
    };
    permissions?: any;
    meta_data?: any;
  }[];
  // Enriched onsite contact from contacts table
  onsite_contact_details?: {
    id: string;
    name: string;
    phone_number?: string;
    email_address?: string;
  };
  // Enriched from the comments schema (loaded onto the shift, not a column).
  comments?: unknown[];
};
